import { useEffect, useMemo, useState } from "react"
import ReactDatePicker from "react-datepicker"
import Select, {
  type TSelectOption,
} from "~/ui-rtk/components/ui/controls/Select"
import { SegmentedToggle } from "~/ui-rtk/components/ui/controls/SegmentedToggle"
import useDate from "~/ui-rtk/hooks/date"
import { TIMEZONES } from "~/ui-rtk/pages/Settings/CompanyPage/timezones"

import "react-datepicker/dist/react-datepicker.css"
import "~/ui-rtk/components/ui/controls/DatePicker/index.scss"

const dayjs = useDate()

export type BlastScheduleMode = "now" | "later"

/**
 * What the parent needs to fire (or schedule) a blast. `iso` is the absolute
 * UTC instant to run at (null when blasting now). `isValid` gates the launch
 * button — false while "later" is selected but the picked instant is missing
 * or in the past.
 */
export interface BlastScheduleValue {
  mode: BlastScheduleMode
  iso: string | null
  isValid: boolean
}

interface ScheduleBlastControlsProps {
  onChange: (value: BlastScheduleValue) => void
  disabled?: boolean
}

const MODE_OPTIONS = [
  { label: "Blast now", value: "now" },
  { label: "Schedule for later", value: "later" },
]

/** The IANA zone the browser is in — the natural default for the picker. */
function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
  } catch {
    return "UTC"
  }
}

/**
 * Interpret the picked wall-clock time in the chosen timezone and return the
 * absolute UTC instant. `react-datepicker` hands us a Date in the browser's
 * local zone, so we take only its wall-clock fields and re-anchor them to the
 * selected zone with dayjs.tz — e.g. "9:00 AM" + "America/New_York" resolves to
 * the correct UTC instant regardless of where the user's browser is.
 */
function resolveIso(date: Date | null, timezone: string): string | null {
  if (!date) return null
  const wall = dayjs(date).format("YYYY-MM-DDTHH:mm:00")
  const instant = dayjs.tz(wall, timezone)
  return instant.isValid() ? instant.toISOString() : null
}

export default function ScheduleBlastControls({
  onChange,
  disabled = false,
}: ScheduleBlastControlsProps) {
  const [mode, setMode] = useState<BlastScheduleMode>("now")
  const [date, setDate] = useState<Date | null>(null)
  const [timezone, setTimezone] = useState<string>(getBrowserTimezone)

  const timezoneOptions = useMemo<TSelectOption[]>(
    () => TIMEZONES.map(({ timezone: tz }) => ({ label: tz, value: tz })),
    [],
  )

  const iso = useMemo(() => resolveIso(date, timezone), [date, timezone])

  // Valid to launch when blasting now, or when a future instant is picked.
  const isValid =
    mode === "now" || (iso !== null && new Date(iso).getTime() > Date.now())

  // Push the resolved state up whenever it changes so the parent can gate its
  // launch button and pass `at` to the blast mutation.
  useEffect(() => {
    onChange({ mode, iso: mode === "later" ? iso : null, isValid })
  }, [mode, iso, isValid, onChange])

  // A human-readable echo of exactly when the blast will fire, rendered in the
  // chosen zone so the user can sanity-check the conversion.
  const runAtLabel = useMemo(() => {
    if (!iso) return null
    return `${dayjs(iso).tz(timezone).format("MMM D, YYYY h:mm A")} (${timezone})`
  }, [iso, timezone])

  return (
    <div className="flex flex-col gap-system-2xs">
      <SegmentedToggle
        options={MODE_OPTIONS}
        value={mode}
        onChange={value => setMode(value as BlastScheduleMode)}
        disabled={disabled}
      />

      {mode === "later" && (
        <div className="flex flex-col gap-system-2xs rounded-md border border-basic-blue bg-basic-black p-system-sm">
          <div className="flex flex-col gap-system-2xs sm:flex-row sm:items-end">
            <div className="flex min-w-0 flex-1 flex-col gap-system-3xs">
              <span className="text-xs text-gray-400">Date &amp; time</span>
              <ReactDatePicker
                selected={date}
                onChange={setDate}
                disabled={disabled}
                showTimeSelect
                timeIntervals={15}
                minDate={new Date()}
                dateFormat="MMM d, yyyy h:mm aa"
                placeholderText="Pick a date and time"
                calendarClassName="ui-rtk-date-picker"
                className="input input-bordered w-full bg-basic-dark-blue"
              />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-system-3xs">
              <span className="text-xs text-gray-400">Timezone</span>
              <Select
                options={timezoneOptions}
                value={timezone}
                disabled={disabled}
                onChange={e => setTimezone(e.target.value)}
              />
            </div>
          </div>

          {runAtLabel && isValid && (
            <span className="text-xs text-gray-300">Will run {runAtLabel}</span>
          )}
          {date && !isValid && (
            <span className="text-xs text-states-error">
              Pick a time in the future.
            </span>
          )}
        </div>
      )}
    </div>
  )
}
